import React, { createContext, useContext, useState, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Order, OrderContextType, OrderStatus } from '../types';
import { useOrg } from './OrgContext';

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrderContext must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const { currentOrganization } = useOrg();

  // Filter orders by current organization
  const orders = allOrders.filter(order => 
    currentOrganization ? order.shopId === currentOrganization.id.toString() : false
  );

  const addOrder = (orderData: Omit<Order, 'id' | 'timestamp' | 'shopId'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    const newOrder: Order = {
      ...orderData,
      id: uuidv4(),
      timestamp: Date.now(),
      shopId: currentOrganization.id.toString(),
    };
    
    setAllOrders(prevOrders => [...prevOrders, newOrder]);
  };

  const updateOrderStatus = (orderId: string, status: OrderStatus) => {
    setAllOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? { ...order, status } : order
      )
    );
  };

  const getOrderById = (orderId: string): Order | undefined => {
    return orders.find(order => order.id === orderId);
  };

  const value: OrderContextType = {
    orders,
    addOrder,
    updateOrderStatus,
    getOrderById,
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
}; 